import { createFileRoute, redirect } from '@tanstack/react-router'
import { Card, Container, Flex, Heading, Text, Badge, Separator, Spinner } from '@radix-ui/themes'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import { ScrollablePageLayout } from '~/components/ScrollablePageLayout'
import { IncidentsStatusSummary } from '~/features/incidents/components/IncidentsStatusSummary'
import LinkButton from '~/components/LinkButton'
import { useQuery } from '@tanstack/react-query'
import { IncidentType, listSiteIncidents, SYSTEM_INCIDENT_TYPES } from '~/features/incidents/service'
import { Tables } from '@willie/supabase'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'
import { LuChartColumnBig, LuSun } from 'react-icons/lu'
import { IoWaterOutline } from 'react-icons/io5'

export const Route = createFileRoute('/sites/$siteID/incidents/')({
  beforeLoad: async ({ context, params }) => {
    const isEnabled = context.postHog?.isFeatureEnabled('incidents-status') // Check if the feature is enabled
    if (import.meta.env.VITE_ENV === 'production' && !isEnabled) {
      throw redirect({
        to: '/sites/$siteID/dashboard',
        params: { siteID: params.siteID }
      }) // Redirect to dashboard if feature is not enabled
    }
  },
  component: RouteComponent
})

type Incident = Tables<'incidents'>

interface IncidentGroup {
  type: IncidentType
  icon: React.ReactNode
  incidents: Incident[]
}

const IncidentCard = ({ incident }: { incident: Incident }) => {
  const { siteID } = Route.useParams()
  const locale = useCurrentLanguage()
  const { t } = useTranslation('sites')

  return (
    <Flex direction="column" gap="2" p="3" className="border-l-2 border-[var(--amber-7)]">
      <Flex align="center" justify="between">
        <Text size="3" weight="medium">
          {incident.title}
        </Text>
        <LinkButton
          variant="ghost"
          size="1"
          to="/sites/$siteID/incidents/$incidentID"
          params={{ siteID, incidentID: incident.id }}
          text={t('incidents.status.viewIncidentDetails')}
          trailingIcon={<ArrowRightIcon />}
        />
      </Flex>
      <Flex align="center" gap="4">
        <Text size="1" color="gray">
          {`${t('incidents.detail.openedSince')} ${DateTime.fromISO(incident.created_at).toLocaleString(
            DateTime.DATETIME_MED_WITH_WEEKDAY,
            { locale }
          )}`}
        </Text>
      </Flex>
    </Flex>
  )
}

const IncidentTypeOverview = ({ incidentGroup }: { incidentGroup: IncidentGroup }) => {
  const { t } = useTranslation('sites')
  const hasIncidents = incidentGroup.incidents.length > 0

  return (
    <Card size="3" className="mb-4">
      <Flex direction="column" gap="3">
        <Flex align="center" gap="3">
          {incidentGroup.icon}
          <Flex direction="column" gap="1">
            <Heading size="4">{t(`incidents.status.types.${incidentGroup.type}`)}</Heading>
            <Text size="2" color="gray">
              {t(`incidents.status.typeDescriptions.${incidentGroup.type}`)}
            </Text>
          </Flex>
          <Flex ml="auto">
            <Badge color={incidentGroup.incidents.length === 0 ? 'green' : 'amber'} variant="soft" size="2">
              {t('incidents.status.activeIncidentsCount', { count: incidentGroup.incidents.length })}
            </Badge>
          </Flex>
        </Flex>

        {hasIncidents && (
          <>
            <Separator size="4" />
            <Flex direction="column" gap="2">
              {incidentGroup.incidents.map((incident) => (
                <IncidentCard key={incident.id} incident={incident} />
              ))}
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  )
}

const incidentTypeIcons: Record<IncidentType, React.ReactNode> = {
  leak: <IoWaterOutline size={28} color="var(--blue-8)" />,
  drought: <LuSun size={28} color="var(--blue-8)" />,
  budget: <LuChartColumnBig size={28} color="var(--blue-8)" />
}

function RouteComponent() {
  const { siteID } = Route.useParams()
  const { t } = useTranslation('sites')

  const { data: incidents = [], isLoading } = useQuery({
    queryKey: ['siteIncidents', siteID],
    queryFn: () => listSiteIncidents(siteID, { status: 'active' })
  })

  // Group incidents by type
  const incidentsByType: Record<IncidentType, Incident[]> = SYSTEM_INCIDENT_TYPES.reduce(
    (acc, type) => ({
      ...acc,
      [type]: incidents.filter((incident) => incident.type === type)
    }),
    {} as Record<IncidentType, Incident[]>
  )

  // Create monitoring categories from real incident data
  const monitoringCategories: IncidentGroup[] = SYSTEM_INCIDENT_TYPES.map((type) => {
    return {
      type: type,
      icon: incidentTypeIcons[type],
      incidents: incidentsByType[type] || []
    }
  })

  if (isLoading) {
    return (
      <ScrollablePageLayout title={t('incidents.status.title')}>
        <Container size="3" py="4">
          <Flex align="center" justify="center" p="8">
            <Spinner size="3" />
          </Flex>
        </Container>
      </ScrollablePageLayout>
    )
  }

  return (
    <ScrollablePageLayout title={t('incidents.status.title')}>
      <Flex direction="column" gap="4">
        <IncidentsStatusSummary siteID={siteID} displayAsHeader variant="outline" />

        {monitoringCategories.map((category) => (
          <IncidentTypeOverview key={category.type} incidentGroup={category} />
        ))}
      </Flex>
    </ScrollablePageLayout>
  )
}
